import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Save, Globe, Image, Phone, Mail, MapPin, Instagram, Facebook, MessageCircle, Plane } from "lucide-react";

interface SiteConfig {
  [key: string]: any;
}

const AdminSettings = () => {
  const [configs, setConfigs] = useState<SiteConfig>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuth();
    fetchConfigs();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }

    const { data: isAdminRes, error: isAdminErr } = await supabase
      .rpc('is_admin', { _uid: session.user.id });

    if (isAdminErr || !isAdminRes) {
      navigate('/');
    }
  };

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('key, value');

      if (error) throw error;

      const configsMap = {};
      data?.forEach(config => {
        configsMap[config.key] = config.value;
      });

      setConfigs(configsMap);
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las configuraciones",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (key: string, value: any) => {
    try {
      const { error } = await supabase
        .from('site_config')
        .update({ value })
        .eq('key', key);

      if (error) throw error;

      setConfigs(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  };

  const saveGeneralSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updateConfig('site_url', { url: configs.site_url?.url || '' }),
        updateConfig('footer_description', { text: configs.footer_description?.text || '' }),
      ]);

      toast({
        title: "Configuración guardada",
        description: "Los ajustes generales se han actualizado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveContactSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updateConfig('contact_email', { email: configs.contact_email?.email || '' }),
        updateConfig('contact_phone', { phone: configs.contact_phone?.phone || '' }),
        updateConfig('contact_location', { address: configs.contact_location?.address || '' }),
      ]);

      toast({
        title: "Contacto actualizado",
        description: "La información de contacto se ha guardado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios de contacto",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveBrandingSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updateConfig('header_logo', { 
          url: configs.header_logo?.url || '',
          alt: configs.header_logo?.alt || 'Travel Tours'
        }),
        updateConfig('footer_logo', { 
          url: configs.footer_logo?.url || '',
          alt: configs.footer_logo?.alt || 'Travel Tours'
        }),
        updateConfig('favicon_url', { url: configs.favicon_url?.url || '' }),
      ]);

      toast({
        title: "Branding actualizado",
        description: "Los logos y favicon se han guardado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios de branding",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const saveSocialSettings = async () => {
    setSaving(true);
    try {
      await Promise.all([
        updateConfig('social_instagram', { 
          url: configs.social_instagram?.url || '', 
          enabled: configs.social_instagram?.enabled || false 
        }),
        updateConfig('social_facebook', { 
          url: configs.social_facebook?.url || '', 
          enabled: configs.social_facebook?.enabled || false 
        }),
        updateConfig('social_tiktok', { 
          url: configs.social_tiktok?.url || '', 
          enabled: configs.social_tiktok?.enabled || false 
        }),
        updateConfig('whatsapp_enabled', { 
          enabled: configs.whatsapp_enabled?.enabled || false,
          number: configs.whatsapp_enabled?.number || ''
        }),
      ]);

      toast({
        title: "Redes sociales actualizadas",
        description: "La configuración de redes sociales se ha guardado",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron guardar los cambios de redes sociales",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            Configuración del Sitio
          </h1>
          <p className="text-muted-foreground mt-2">
            Gestiona la configuración y personalización de tu sitio web
          </p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="contact">Contacto</TabsTrigger>
            <TabsTrigger value="social">Redes Sociales</TabsTrigger>
            <TabsTrigger value="transfers">Transfers</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="h-5 w-5" />
                  <span>Configuración General</span>
                </CardTitle>
                <CardDescription>
                  Configura los ajustes básicos de tu sitio web
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="site-url">URL del Sitio</Label>
                  <Input
                    id="site-url"
                    value={configs.site_url?.url || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      site_url: { url: e.target.value }
                    }))}
                    placeholder="https://tudominio.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="footer-description">Descripción del Footer</Label>
                  <Textarea
                    id="footer-description"
                    value={configs.footer_description?.text || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      footer_description: { text: e.target.value }
                    }))}
                    placeholder="Descripción que aparecerá en el footer del sitio"
                    rows={3}
                  />
                </div>

                <Button onClick={saveGeneralSettings} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="branding" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Image className="h-5 w-5" />
                  <span>Imágenes y Logos</span>
                </CardTitle>
                <CardDescription>
                  Gestiona los logos y el favicon de tu sitio
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="header-logo">Logo del Header</Label>
                  <Input
                    id="header-logo"
                    value={configs.header_logo?.url || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      header_logo: { url: e.target.value, alt: 'Travel Tours' }
                    }))}
                    placeholder="URL del logo para el header"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="footer-logo">Logo del Footer</Label>
                  <Input
                    id="footer-logo"
                    value={configs.footer_logo?.url || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      footer_logo: { url: e.target.value, alt: 'Travel Tours' }
                    }))}
                    placeholder="URL del logo para el footer"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="favicon">Favicon</Label>
                  <Input
                    id="favicon"
                    value={configs.favicon_url?.url || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      favicon_url: { url: e.target.value }
                    }))}
                    placeholder="URL del favicon (.png, .jpg)"
                  />
                </div>

                <Button onClick={saveBrandingSettings} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Guardando...' : 'Guardar Branding'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Phone className="h-5 w-5" />
                  <span>Información de Contacto</span>
                </CardTitle>
                <CardDescription>
                  Configura los datos de contacto de tu empresa
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>Correo Electrónico</span>
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={configs.contact_email?.email || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      contact_email: { email: e.target.value }
                    }))}
                    placeholder="info@traveltours.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-phone" className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" />
                    <span>Teléfono</span>
                  </Label>
                  <Input
                    id="contact-phone"
                    value={configs.contact_phone?.phone || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      contact_phone: { phone: e.target.value }
                    }))}
                    placeholder="+1-800-TRAVEL"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-location" className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Ubicación</span>
                  </Label>
                  <Input
                    id="contact-location"
                    value={configs.contact_location?.address || ''}
                    onChange={(e) => setConfigs(prev => ({
                      ...prev,
                      contact_location: { address: e.target.value }
                    }))}
                    placeholder="123 Travel Street, Adventure City"
                  />
                </div>

                <Button onClick={saveContactSettings} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Guardando...' : 'Guardar Contacto'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="social" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Instagram className="h-5 w-5" />
                  <span>Redes Sociales</span>
                </CardTitle>
                <CardDescription>
                  Configura tus perfiles en redes sociales y WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="instagram" className="flex items-center space-x-2">
                        <Instagram className="h-4 w-4" />
                        <span>Instagram</span>
                      </Label>
                      <Switch
                        checked={configs.social_instagram?.enabled || false}
                        onCheckedChange={(enabled) => setConfigs(prev => ({
                          ...prev,
                          social_instagram: { ...prev.social_instagram, enabled }
                        }))}
                      />
                    </div>
                    <Input
                      id="instagram"
                      value={configs.social_instagram?.url || ''}
                      onChange={(e) => setConfigs(prev => ({
                        ...prev,
                        social_instagram: { ...prev.social_instagram, url: e.target.value }
                      }))}
                      placeholder="https://instagram.com/tuusuario"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="facebook" className="flex items-center space-x-2">
                        <Facebook className="h-4 w-4" />
                        <span>Facebook</span>
                      </Label>
                      <Switch
                        checked={configs.social_facebook?.enabled || false}
                        onCheckedChange={(enabled) => setConfigs(prev => ({
                          ...prev,
                          social_facebook: { ...prev.social_facebook, enabled }
                        }))}
                      />
                    </div>
                    <Input
                      id="facebook"
                      value={configs.social_facebook?.url || ''}
                      onChange={(e) => setConfigs(prev => ({
                        ...prev,
                        social_facebook: { ...prev.social_facebook, url: e.target.value }
                      }))}
                      placeholder="https://facebook.com/tupagina"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="tiktok" className="flex items-center space-x-2">
                        <span>🎵</span>
                        <span>TikTok</span>
                      </Label>
                      <Switch
                        checked={configs.social_tiktok?.enabled || false}
                        onCheckedChange={(enabled) => setConfigs(prev => ({
                          ...prev,
                          social_tiktok: { ...prev.social_tiktok, enabled }
                        }))}
                      />
                    </div>
                    <Input
                      id="tiktok"
                      value={configs.social_tiktok?.url || ''}
                      onChange={(e) => setConfigs(prev => ({
                        ...prev,
                        social_tiktok: { ...prev.social_tiktok, url: e.target.value }
                      }))}
                      placeholder="https://tiktok.com/@tuusuario"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="whatsapp" className="flex items-center space-x-2">
                        <MessageCircle className="h-4 w-4" />
                        <span>WhatsApp</span>
                      </Label>
                      <Switch
                        checked={configs.whatsapp_enabled?.enabled || false}
                        onCheckedChange={(enabled) => setConfigs(prev => ({
                          ...prev,
                          whatsapp_enabled: { ...prev.whatsapp_enabled, enabled }
                        }))}
                      />
                    </div>
                    <Input
                      id="whatsapp"
                      value={configs.whatsapp_enabled?.number || ''}
                      onChange={(e) => setConfigs(prev => ({
                        ...prev,
                        whatsapp_enabled: { ...prev.whatsapp_enabled, number: e.target.value }
                      }))}
                      placeholder="1234567890 (sin + ni espacios)"
                    />
                  </div>
                </div>

                <Button onClick={saveSocialSettings} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Guardando...' : 'Guardar Redes Sociales'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transfers" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Plane className="h-5 w-5" />
                  <span>Configuración de Aeropuertos</span>
                </CardTitle>
                <CardDescription>
                  Habilita o deshabilita aeropuertos para el sistema de transfers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {(() => {
                  const airportsConfig = configs.airports_config || {};
                  const airports = [
                    { id: 'punta_cana_airport', name: 'Punta Cana Intl. Airport', code: 'PUJ' },
                    { id: 'la_romana_airport', name: 'La Romana Intl. Airport', code: 'LRM' },
                    { id: 'las_americas_airport', name: 'Las Americas Intl. Airport', code: 'SDQ' },
                    { id: 'la_isabela_airport', name: 'La Isabela Airport', code: 'JBQ' },
                    { id: 'puerto_plata_airport', name: 'Puerto Plata Airport', code: 'POP' },
                    { id: 'samana_airport', name: 'Samana El Catey Airport', code: 'AZS' },
                    { id: 'santiago_airport', name: 'Santiago Airport', code: 'STI' },
                  ];

                  return (
                    <div className="space-y-4">
                      {airports.map((airport) => {
                        const config = airportsConfig[airport.id] || { enabled: true };
                        return (
                          <div key={airport.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex items-center space-x-3">
                              <Plane className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{airport.name}</p>
                                <p className="text-sm text-muted-foreground">Código: {airport.code}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm ${config.enabled ? 'text-green-600' : 'text-red-600'}`}>
                                {config.enabled ? 'Activo' : 'Inactivo'}
                              </span>
                              <Switch
                                checked={config.enabled}
                                onCheckedChange={(enabled) => {
                                  const newConfig = {
                                    ...airportsConfig,
                                    [airport.id]: {
                                      ...config,
                                      enabled,
                                      name: `${airport.name} (${airport.code})`,
                                      code: airport.code
                                    }
                                  };
                                  setConfigs(prev => ({
                                    ...prev,
                                    airports_config: newConfig
                                  }));
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <Button 
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await updateConfig('airports_config', configs.airports_config || {});
                      toast({
                        title: "Aeropuertos actualizados",
                        description: "La configuración de aeropuertos se ha guardado correctamente",
                      });
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "No se pudieron guardar los cambios de aeropuertos",
                        variant: "destructive"
                      });
                    } finally {
                      setSaving(false);
                    }
                  }} 
                  disabled={saving}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Guardando...' : 'Guardar Aeropuertos'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;